import { useState, useEffect } from "react";
import { useNavigate, useLocation, Routes, Route, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { authUtils } from "@/utils/auth";
import {
  LayoutDashboard, Calendar, DollarSign, Mail, LogOut, Loader2,
  Search, Filter, Eye, Edit2, X, Check, Clock, Users, TrendingUp,
  ChevronDown, Menu, Gift, Lock, Crown
} from "lucide-react";
import PromoCodeManagement from "./PromoCodeManagement";
import BlockedSlotsManagement from "./BlockedSlotsManagement";
import MembershipManagement from "./MembershipManagement";
import CourseSpotsManager from "./CourseSpotsManager";
import JuniorProgressManager from "./JuniorProgressManager";
import { sessionDate, sessionTime } from "@/components/junior/SessionChecklist";
import { GraduationCap } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dashboard Stats Card
function StatCard({ title, value, icon: Icon, color = "violet" }) {
  const colors = {
    violet: "bg-dream-violet/20 text-dream-violet",
    green: "bg-green-500/20 text-green-500",
    yellow: "bg-yellow-500/20 text-yellow-500",
    blue: "bg-blue-500/20 text-blue-500"
  };

  return (
    <div className="glass rounded-xl p-6" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <p className="text-white/60 text-sm mb-1">{title}</p>
      <p className="font-heading text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

// Pricing Row Component — explicit Save button + Enter key support
function PricingRow({ serviceId, service, onSave }) {
  const [value, setValue] = useState(service.price.toString());
  const [saving, setSaving] = useState(false);
  const dirty = value !== service.price.toString();

  const commit = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await onSave(serviceId, value);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-4 bg-dream-surface rounded-xl">
      <div className="min-w-0 flex-1">
        <p className="text-white font-medium truncate">{service.name}</p>
        <p className="text-white/50 text-sm">Type: {service.type} | Unit: {service.unit}</p>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <span className="text-white/60">$</span>
        <Input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
          onBlur={commit}
          className="w-24 bg-dream-bg border-dream-border text-white text-right"
          data-testid={`pricing-input-${serviceId}`}
        />
        <Button
          onClick={commit}
          disabled={!dirty || saving}
          className="bg-dream-violet hover:bg-dream-violet/90 disabled:opacity-40 disabled:cursor-not-allowed text-white"
          data-testid={`pricing-save-${serviceId}`}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

// Export Bookings Bar — lets admin download a multi-sheet Excel of bookings filtered by date range and payment status.
function ExportBookingsBar() {
  const [from, setFrom] = useState(() => new Date(Date.now() - 1000 * 60 * 60 * 24 * 180).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [category, setCategory] = useState("all");
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = { date_from: from, date_to: to, payment_status: paymentStatus };
      if (category && category !== "all") params.category = category;
      const res = await axios.get(`${API}/admin/bookings/export-xlsx`, {
        ...authUtils.getAuthConfig(),
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const catTag = category === "all" ? "AllCategories" : category;
      a.download = `DMRG_Bookings_${catTag}_${from}_to_${to}_${paymentStatus}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (e) {
      console.error("export failed", e);
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const handleQuickDjExport = async () => {
    // One-click DJ Room clients export — all statuses, no date bounds.
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings/export-xlsx`, {
        ...authUtils.getAuthConfig(),
        params: { payment_status: "all", category: "dj_room" },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DMRG_DJRoom_Clients_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("DJ Room clients Excel downloaded");
    } catch (e) {
      console.error("dj export failed", e);
      toast.error(e.response?.data?.detail || "DJ export failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-4 border-b border-dream-border bg-dream-surface/30 space-y-3">
      <div className="flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex-1">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-1">From</p>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="bg-dream-bg border-dream-border text-white"
            data-testid="export-date-from"
          />
        </div>
        <div className="flex-1">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-1">To</p>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="bg-dream-bg border-dream-border text-white"
            data-testid="export-date-to"
          />
        </div>
        <div className="flex-1">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Category</p>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="bg-dream-bg border-dream-border text-white" data-testid="export-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-dream-surface border-dream-border text-white">
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="dj_room">DJ Room (Hire + Lessons)</SelectItem>
              <SelectItem value="recording_studio">Recording Studio</SelectItem>
              <SelectItem value="production_room">Production Room</SelectItem>
              <SelectItem value="photography">Photography</SelectItem>
              <SelectItem value="mixing_mastering">Mixing &amp; Mastering</SelectItem>
              <SelectItem value="rehearsals">Rehearsals</SelectItem>
              <SelectItem value="music_lessons">Music Lessons (archived)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Payment Status</p>
          <Select value={paymentStatus} onValueChange={setPaymentStatus}>
            <SelectTrigger className="bg-dream-bg border-dream-border text-white" data-testid="export-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-dream-surface border-dream-border text-white">
              <SelectItem value="paid">Paid only</SelectItem>
              <SelectItem value="pending">Pending only</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={handleDownload}
          disabled={downloading || !from || !to}
          className="bg-dream-violet hover:bg-dream-violet/90 text-white h-10"
          data-testid="export-xlsx-btn"
        >
          {downloading ? "Generating…" : "Download Excel"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={handleQuickDjExport}
          disabled={downloading}
          variant="outline"
          className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 h-9 text-xs"
          data-testid="export-dj-clients-btn"
        >
          {downloading ? "Generating…" : "Quick: All DJ Room clients (all-time)"}
        </Button>
        <ReconcileSquareButton />
      </div>
    </div>
  );
}


// Reconcile Square Payments — cross-checks pending Square bookings against Square's API
// and auto-marks any that Square confirms as paid. Emails go out to the customer + studio.
function ReconcileSquareButton() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const runReconcile = async () => {
    if (!window.confirm("Cross-check all pending Square bookings from the last 30 days against Square? Any confirmed-paid ones will be auto-marked and confirmation emails will be sent.")) return;
    setRunning(true);
    setReport(null);
    try {
      const res = await axios.post(`${API}/admin/bookings/reconcile-square?days=30`, {}, authUtils.getAuthConfig());
      setReport(res.data);
      const r = res.data.reconciled_paid;
      const u = res.data.still_unpaid;
      if (r > 0) toast.success(`Reconciled ${r} paid · ${u} still pending`);
      else toast.info(`Checked ${res.data.total_pending_checked} — none newly confirmed paid`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconcile failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Button
        onClick={runReconcile}
        disabled={running}
        variant="outline"
        className="border-green-500/40 text-green-300 hover:bg-green-500/10 h-9 text-xs"
        data-testid="reconcile-square-btn"
      >
        {running ? "Reconciling…" : "Reconcile Square Payments"}
      </Button>
      {report && (
        <div className="w-full mt-3 p-4 rounded-xl bg-black/30 border border-white/10 text-xs">
          <div className="flex flex-wrap gap-4 mb-2">
            <span className="text-white/70">Checked: <b className="text-white">{report.total_pending_checked}</b></span>
            <span className="text-green-400">Newly paid: <b>{report.reconciled_paid}</b></span>
            <span className="text-yellow-400">Still pending: <b>{report.still_unpaid}</b></span>
            {report.errors > 0 && <span className="text-red-400">Errors: <b>{report.errors}</b></span>}
          </div>
          {report.reconciled_bookings && report.reconciled_bookings.length > 0 && (
            <div className="mt-2 text-green-300">
              <p className="font-semibold mb-1">Now marked paid:</p>
              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                {report.reconciled_bookings.map((b) => (
                  <li key={b.id}>· {b.customer} · {b.email} · ${b.amount}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// Booking Row Component
function BookingRow({ booking, onView, onStatusChange, canEdit }) {
  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-500",
    paid: "bg-green-500/20 text-green-500",
    confirmed: "bg-blue-500/20 text-blue-500",
    cancelled: "bg-red-500/20 text-red-500",
    completed: "bg-gray-500/20 text-gray-400"
  };

  return (
    <tr className="border-b border-dream-border hover:bg-white/5 transition-colors">
      <td className="px-4 py-4">
        <p className="font-medium text-white">{booking.full_name}</p>
        <p className="text-white/50 text-sm">{booking.email}</p>
      </td>
      <td className="px-4 py-4 text-white/80">{booking.service_name}</td>
      <td className="px-4 py-4 text-white/80">{booking.booking_date}</td>
      <td className="px-4 py-4">
        <span className="font-mono text-dream-violet">${booking.total_amount?.toFixed(2)}</span>
      </td>
      <td className="px-4 py-4">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[booking.payment_status] || statusColors.pending}`}>
          {booking.payment_status}
        </span>
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onView(booking)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-white/60" />
          </button>
          {canEdit && (
            <Select
              value={booking.payment_status}
              onValueChange={(value) => onStatusChange(booking.id, value)}
            >
              <SelectTrigger className="w-[110px] h-8 text-xs bg-transparent border-dream-border">
                <Edit2 className="w-3 h-3 mr-1" />
                <span>Status</span>
              </SelectTrigger>
              <SelectContent className="bg-dream-surface border-dream-border">
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </td>
    </tr>
  );
}

// Booking Detail Modal
function BookingDetailModal({ booking, onClose }) {
  const [actionLoading, setActionLoading] = useState(null);
  if (!booking) return null;

  const resendConfirmation = async () => {
    if (!window.confirm(`Resend booking confirmation to ${booking.email} and admin notification (with calendar invite)?`)) return;
    setActionLoading('resend');
    try {
      const res = await axios.post(`${API}/admin/bookings/${booking.id}/resend-confirmation`, {}, authUtils.getAuthConfig());
      toast.success(res.data.message || 'Confirmation re-sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend');
    } finally {
      setActionLoading(null);
    }
  };

  const markAsPaid = async () => {
    if (!window.confirm(`Mark this booking as PAID and send confirmation emails? Use this only if Square/Stripe took the money but the booking is stuck as pending.`)) return;
    setActionLoading('paid');
    try {
      const res = await axios.post(`${API}/admin/bookings/${booking.id}/mark-paid`, {}, authUtils.getAuthConfig());
      toast.success(res.data.message || 'Booking marked paid');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark paid');
    } finally {
      setActionLoading(null);
    }
  };

  const unmarkPaid = async () => {
    if (!window.confirm(`Revert this booking back to PENDING? Use this ONLY if it was incorrectly marked paid (e.g. no matching payment in Square). No emails will be sent.`)) return;
    setActionLoading('unpaid');
    try {
      const res = await axios.post(`${API}/admin/bookings/${booking.id}/unmark-paid`, {}, authUtils.getAuthConfig());
      toast.success(res.data.message || 'Booking reverted to pending');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unmark paid');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold text-white">Booking Details</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-white/50 text-sm">Customer</p>
              <p className="text-white font-medium">{booking.full_name}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm">Artist Name</p>
              <p className="text-white">{booking.artist_name || "N/A"}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm">Email</p>
              <p className="text-white">{booking.email}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm">Phone</p>
              <p className="text-white">{booking.phone}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm">Service</p>
              <p className="text-white">{booking.service_name}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm">Date</p>
              <p className="text-white">{booking.booking_date}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm">Time Slots</p>
              <p className="text-white">
                {booking.junior_details && booking.time_slots?.length > 0
                  ? `${sessionTime(booking.time_slots[0])} (1.5 hrs)`
                  : booking.time_slots?.length > 0 
                  ? `${booking.time_slots[0]}:00 - ${booking.time_slots[booking.time_slots.length - 1] + 1}:00`
                  : "N/A"
                }
              </p>
            </div>
            <div>
              <p className="text-white/50 text-sm">Total Amount</p>
              <p className="text-dream-violet font-mono text-lg">${booking.total_amount?.toFixed(2)} AUD</p>
            </div>
            <div>
              <p className="text-white/50 text-sm">Status</p>
              <p className="text-white capitalize">{booking.payment_status}</p>
            </div>
            <div>
              <p className="text-white/50 text-sm">Created</p>
              <p className="text-white text-sm">{new Date(booking.created_at).toLocaleString()}</p>
            </div>
          </div>

          {booking.junior_details && (
            <div className="mt-4 pt-4 border-t border-dream-border" data-testid="admin-junior-details">
              <p className="text-lime-300 text-sm font-semibold mb-3">Junior DJ Academy · booked by {booking.junior_details.booked_by === "student" ? "student (call parent to verify)" : "parent/guardian"}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[["Parent", "parent_name"], ["Parent mobile", "parent_mobile"], ["Suburb", "suburb"], ["Student", "student_name"], ["Age", "student_age"], ["Experience", "experience"], ["Genres", "genres"]].map(([l, k]) => (
                  <div key={k}><p className="text-white/50">{l}</p><p className="text-white">{booking.junior_details[k] || "—"}</p></div>
                ))}
              </div>
              {booking.junior_details.sibling && (
                <p className="text-pink-300 text-sm mt-3" data-testid="admin-junior-sibling">Sibling (15% off): {booking.junior_details.sibling.student_name} · age {booking.junior_details.sibling.student_age} · {booking.junior_details.sibling.experience} · {booking.junior_details.sibling.genres}</p>
              )}
              {booking.extra_sessions?.length > 0 && (
                <div className="mt-3">
                  <p className="text-white/50 text-sm">All sessions</p>
                  <ul className="text-white text-sm list-disc ml-5">
                    {[{ date: booking.booking_date, time_slots: booking.time_slots }, ...booking.extra_sessions].map((s) => (
                      <li key={`${s.date}-${s.student || 1}`}>{sessionDate(s.date)} · {sessionTime(s.time_slots[0])} (1.5 hrs){s.student === 2 ? " · sibling" : ""}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {booking.id_document && (
            <div className="mt-4 pt-4 border-t border-dream-border">
              <p className="text-white/50 text-sm mb-2">ID Document</p>
              <img 
                src={booking.id_document} 
                alt="ID Document" 
                className="max-w-full rounded-lg"
              />
            </div>
          )}

          {/* Admin action buttons */}
          <div className="mt-6 pt-4 border-t border-dream-border space-y-2">
            <p className="text-white/40 text-xs mb-2">Admin actions</p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={resendConfirmation}
                disabled={!!actionLoading}
                className="bg-dream-violet hover:bg-dream-violet/90 text-white text-sm"
                data-testid="resend-confirmation-btn"
              >
                {actionLoading === 'resend' ? 'Sending…' : '📧 Resend Confirmation Email'}
              </Button>
              {booking.payment_status !== 'paid' && (
                <Button
                  onClick={markAsPaid}
                  disabled={!!actionLoading}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm"
                  data-testid="mark-paid-btn"
                >
                  {actionLoading === 'paid' ? 'Updating…' : 'Mark as Paid + Send Confirmation'}
                </Button>
              )}
              {booking.payment_status === 'paid' && (
                <Button
                  onClick={unmarkPaid}
                  disabled={!!actionLoading}
                  variant="outline"
                  className="border-red-500/50 text-red-300 hover:bg-red-500/10 text-sm bg-transparent"
                  data-testid="unmark-paid-btn"
                >
                  {actionLoading === 'unpaid' ? 'Reverting…' : 'Revert to Pending (if not actually paid)'}
                </Button>
              )}
            </div>
            <p className="text-white/30 text-xs">
              &quot;Mark as Paid&quot; only if Square took the customer&apos;s money but the booking is stuck as pending. Use &quot;Revert to Pending&quot; if a booking was incorrectly marked paid.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Main Dashboard Component
export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [pricing, setPricing] = useState({});
  const [canEditPricing, setCanEditPricing] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [activeTab, setActiveTab] = useState("bookings");
  const [filters, setFilters] = useState({ status: "", search: "" });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle session_id in hash (from OAuth callback) and auth check
  useEffect(() => {
    let isMounted = true;
    let authCompleted = false;
    
    // Safety timeout - only triggers if auth init is still hanging after 10s
    const safetyTimeout = setTimeout(() => {
      if (isMounted && !authCompleted) {
        authUtils.clearToken();
        setLoading(false);
        navigate("/admin/login", { replace: true, state: { error: "Session timed out. Please log in again." } });
      }
    }, 10000);
    
    const initAuth = async () => {
      // Handle Emergent OAuth callback — when user returns from Google sign-in,
      // the URL is /admin/dashboard#session_id=xxx. We exchange the session_id
      // for a backend token, store it, then strip the hash in-place. No navigation
      // needed since we are already on /admin/dashboard, which avoids redirect loops.
      const hash = window.location.hash;
      if (hash?.includes('session_id=') && !authUtils.hasToken()) {
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');
        if (sessionId) {
          try {
            const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
            if (response.data?.user && response.data?.session_token) {
              authUtils.setToken(response.data.session_token);
              // Strip hash from URL so a refresh won't re-trigger the exchange
              window.history.replaceState(null, '', '/admin/dashboard');
              authCompleted = true;
              setUser(response.data.user);
              setLoading(false);
              return;
            }
            throw new Error('No user data or token received');
          } catch (error) {
            console.error('AdminDashboard: session exchange failed', error);
            const errorMsg = error.response?.data?.detail || 'Authentication failed';
            authCompleted = true;
            navigate('/admin/login', { replace: true, state: { error: errorMsg } });
            return;
          }
        }
      }

      // If we have user from navigation state, also make sure we have a token
      if (location.state?.user) {
        if (!authUtils.hasToken()) {
          authCompleted = true;
          navigate("/admin/login", { replace: true });
          return;
        }
        authCompleted = true;
        setUser(location.state.user);
        setLoading(false);
        return;
      }

      // Check if we have a stored token
      if (authUtils.hasToken()) {
        try {
          const response = await axios.get(`${API}/auth/me`, authUtils.getAuthConfig());
          authCompleted = true;
          setUser(response.data);
          setLoading(false);
          return;
        } catch (error) {
          console.error("AdminDashboard: Token invalid, clearing", error);
          authUtils.clearToken();
          authCompleted = true;
          setLoading(false);
        }
      }
      
      // No valid auth, redirect to login
      authCompleted = true;
      setLoading(false);
      navigate("/admin/login", { replace: true });
    };
    
    initAuth();
    
    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.state]);

  // Fetch data when user is authenticated
  useEffect(() => {
    if (!user) return;
    fetchStats();
    fetchBookings();
    if (user.permissions?.can_view_contacts) {
      fetchContacts();
    }
    // Fetch pricing if user can edit (main admin or partner with edit rights)
    if (user.permissions?.can_edit_pricing) {
      fetchPricing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, authUtils.getAuthConfig());
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      const res = await axios.get(`${API}/admin/bookings?${params}`, authUtils.getAuthConfig());
      setBookings(res.data.bookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, authUtils.getAuthConfig());
      setContacts(res.data.contacts);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/admin/pricing`, authUtils.getAuthConfig());
      setPricing(res.data.pricing);
      setCanEditPricing(res.data.can_edit);
    } catch (error) {
      console.error("Error fetching pricing:", error);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}`, 
        { status: newStatus },
        authUtils.getAuthConfig()
      );
      toast.success(`Booking status updated to ${newStatus}`);
      fetchBookings();
      fetchStats();
    } catch (error) {
      toast.error("Failed to update booking status");
    }
  };

  const handlePriceUpdate = async (serviceId, newPrice) => {
    try {
      await axios.put(`${API}/admin/pricing/${serviceId}`,
        { service_id: serviceId, price: parseFloat(newPrice) },
        authUtils.getAuthConfig()
      );
      toast.success("Price updated successfully");
      fetchPricing();
    } catch (error) {
      toast.error("Failed to update price");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, authUtils.getAuthConfig());
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Always clear token and redirect
      authUtils.clearToken();
      navigate("/admin/login", { replace: true });
    }
  };

  // Filter bookings by search
  const filteredBookings = bookings.filter(b => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      b.full_name?.toLowerCase().includes(search) ||
      b.email?.toLowerCase().includes(search) ||
      b.service_name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-dream-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-dream-violet animate-spin" />
      </div>
    );
  }

  const isDJAdmin = user?.role === "dj_admin";

  return (
    <div className="min-h-screen bg-dream-bg" data-testid="admin-dashboard">
      {/* Top Bar */}
      <header className="glass border-b border-dream-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              className="md:hidden p-2 hover:bg-white/10 rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-5 h-5 text-white" />
            </button>
            <img 
              src="https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/w4q79k1x_Dream%20Maker-4.png" 
              alt="Dream Makers"
              className="h-10 w-auto"
            />
            <span className="hidden sm:block text-white/60 text-sm border-l border-dream-border pl-4">
              Admin Dashboard
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2">
              {user?.picture && (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              )}
              <div className="text-right">
                <p className="text-white text-sm font-medium">{user?.name}</p>
                <p className="text-white/50 text-xs capitalize">{user?.role?.replace("_", " ")}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-white/60 hover:text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mb-6 glass rounded-xl p-4">
            <nav className="space-y-2">
              <button
                onClick={() => { setActiveTab("bookings"); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "bookings" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
              >
                <Calendar className="w-5 h-5" />
                Bookings
              </button>
              {user?.permissions?.can_edit_pricing && (
                <button
                  onClick={() => { setActiveTab("pricing"); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "pricing" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
                >
                  <DollarSign className="w-5 h-5" />
                  Pricing
                </button>
              )}
              <button
                onClick={() => { setActiveTab("availability"); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "availability" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
              >
                <Lock className="w-5 h-5" />
                Availability
              </button>
              {user?.role === "main_admin" && (
                <button
                  onClick={() => { setActiveTab("promo"); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "promo" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
                >
                  <Gift className="w-5 h-5" />
                  Promo Codes
                </button>
              )}
              {user?.role === "main_admin" && (
                <button
                  onClick={() => { setActiveTab("memberships"); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "memberships" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
                >
                  <Crown className="w-5 h-5" />
                  Memberships
                </button>
              )}
              {user?.role === "main_admin" && (
                <button
                  onClick={() => { setActiveTab("junior"); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "junior" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
                  data-testid="admin-mobile-tab-junior"
                >
                  <GraduationCap className="w-5 h-5" />
                  Junior Academy
                </button>
              )}
              {user?.permissions?.can_view_contacts && (
                <button
                  onClick={() => { setActiveTab("contacts"); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === "contacts" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
                >
                  <Mail className="w-5 h-5" />
                  Contacts
                </button>
              )}
            </nav>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Bookings"
            value={stats?.total_bookings || 0}
            icon={Calendar}
            color="violet"
          />
          <StatCard
            title="Revenue"
            value={`$${(stats?.total_revenue || 0).toLocaleString()}`}
            icon={TrendingUp}
            color="green"
          />
          <StatCard
            title="Pending"
            value={stats?.pending_bookings || 0}
            icon={Clock}
            color="yellow"
          />
          <StatCard
            title={isDJAdmin ? "DJ Bookings" : "Today"}
            value={stats?.today_bookings || 0}
            icon={Users}
            color="blue"
          />
        </div>

        {/* Desktop Tabs */}
        <div className="hidden md:flex items-center gap-4 mb-6">
          <button
            onClick={() => setActiveTab("bookings")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === "bookings" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
          >
            <Calendar className="w-4 h-4" />
            Bookings
          </button>
          {user?.permissions?.can_edit_pricing && (
            <button
              onClick={() => setActiveTab("pricing")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === "pricing" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
            >
              <DollarSign className="w-4 h-4" />
              Pricing
            </button>
          )}
          <button
            onClick={() => setActiveTab("availability")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === "availability" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
          >
            <Lock className="w-4 h-4" />
            Availability
          </button>
          {user?.role === "main_admin" && (
            <button
              onClick={() => setActiveTab("promo")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === "promo" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
            >
              <Gift className="w-4 h-4" />
              Promo Codes
            </button>
          )}
          {user?.role === "main_admin" && (
            <button
              onClick={() => setActiveTab("memberships")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === "memberships" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
            >
              <Crown className="w-4 h-4" />
              Memberships
            </button>
          )}
          {user?.role === "main_admin" && (
            <button
              onClick={() => setActiveTab("junior")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === "junior" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
              data-testid="admin-tab-junior"
            >
              <GraduationCap className="w-4 h-4" />
              Junior Academy
            </button>
          )}
          {user?.permissions?.can_view_contacts && (
            <button
              onClick={() => setActiveTab("contacts")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === "contacts" ? "bg-dream-violet text-white" : "text-white/60 hover:bg-white/10"}`}
            >
              <Mail className="w-4 h-4" />
              Contacts ({contacts.length})
            </button>
          )}
        </div>

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="glass rounded-2xl overflow-hidden">
            {/* Export bar */}
            <ExportBookingsBar />
            {/* Filters */}
            <div className="p-4 border-b border-dream-border flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  placeholder="Search by name, email, or service..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 bg-dream-surface border-dream-border text-white placeholder:text-white/30"
                />
              </div>
              <Select
                value={filters.status}
                onValueChange={(value) => {
                  setFilters({ ...filters, status: value === "all" ? "" : value });
                  setTimeout(fetchBookings, 100);
                }}
              >
                <SelectTrigger className="w-full sm:w-[150px] bg-dream-surface border-dream-border text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-dream-surface border-dream-border">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-dream-surface/50">
                    <th className="px-4 py-3 text-left text-sm font-medium text-white/60">Customer</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-white/60">Service</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-white/60">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-white/60">Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-white/60">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-white/60">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.length > 0 ? (
                    filteredBookings.map((booking) => (
                      <BookingRow
                        key={booking.id}
                        booking={booking}
                        onView={async (b) => {
                          // Show modal immediately with list-level data, then enrich with id_document
                          // (excluded from list query for performance — fetched per-booking).
                          setSelectedBooking(b);
                          try {
                            const res = await axios.get(`${API}/admin/bookings/${b.id}`, authUtils.getAuthConfig());
                            setSelectedBooking(res.data);
                          } catch (err) {
                            console.error('Failed to load full booking detail', err);
                          }
                        }}
                        onStatusChange={handleStatusChange}
                        canEdit={true}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-white/50">
                        No bookings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === "pricing" && canEditPricing && !user?.permissions?.allowed_services && <CourseSpotsManager />}
        {activeTab === "pricing" && canEditPricing && (
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold text-white">Service Pricing</h2>
              {user?.permissions?.allowed_services && (
                <span className="text-white/50 text-sm">
                  Editing your services only
                </span>
              )}
            </div>
            <div className="space-y-4">
              {Object.keys(pricing).length > 0 ? (
                Object.entries(pricing).map(([serviceId, service]) => (
                  <PricingRow
                    key={serviceId}
                    serviceId={serviceId}
                    service={service}
                    onSave={handlePriceUpdate}
                  />
                ))
              ) : (
                <div className="text-center py-12 text-white/50">
                  No services available for pricing edit
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && user?.permissions?.can_view_contacts && (
          <div className="glass rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-dream-border">
              <h2 className="font-heading text-xl font-bold text-white">Contact Submissions</h2>
            </div>
            <div className="divide-y divide-dream-border">
              {contacts.length > 0 ? (
                contacts.map((contact) => (
                  <div key={contact.id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-white font-medium">{contact.full_name}</p>
                        <p className="text-white/50 text-sm">{contact.email} • {contact.phone}</p>
                      </div>
                      <p className="text-white/40 text-xs">
                        {new Date(contact.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <p className="text-white/70 text-sm">{contact.message}</p>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-white/50">
                  No contact submissions yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === "availability" && (
          <BlockedSlotsManagement user={user} />
        )}

        {/* Promo Codes Tab */}
        {activeTab === "promo" && (
          <PromoCodeManagement />
        )}

        {/* Memberships Tab */}
        {activeTab === "memberships" && (
          <MembershipManagement />
        )}
        {activeTab === "junior" && user?.role === "main_admin" && <JuniorProgressManager />}
      </div>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
        />
      )}
    </div>
  );
}
